<!-- ── Floating button ──────────────────────────────────────────────────── -->
<button
  #fabBtn
  class="cb-fab"
  [class.cb-fab--open]="isOpen"
  [style.left.px]="fabLeft"
  [style.bottom.px]="fabBottom"
  (mousedown)="onFabDragStart($event)"
  (touchstart)="onFabDragStart($event)"
  (click)="onFabClick()"
  aria-label="פתח בוט">
  @if (!isOpen) {
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="white">
      <path d="M12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.58 1.37 5.07L2 22l5.09-1.35A9.95 9.95 0 0 0 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
    </svg>
  }
  @if (isOpen) {
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="white">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
    </svg>
  }
  @if (!isOpen && unreadCount > 0) {
    <span class="cb-fab__badge">{{ unreadCount }}</span>
  }
</button>

<!-- ── Chat panel ────────────────────────────────────────────────────────── -->
<div
  #panel
  class="cb-panel"
  [class.cb-panel--open]="isOpen"
  [class.cb-panel--wide]="wide"
  [style.left.px]="panelLeft"
  [style.bottom.px]="panelBottom"
  dir="rtl">

  <!-- Header — drag handle -->
  <div
    class="cb-header"
    (mousedown)="onHeaderDragStart($event)"
    (touchstart)="onHeaderDragStart($event)">
    <div class="cb-header__avatar">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="white">
        <path d="M12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.58 1.37 5.07L2 22l5.09-1.35A9.95 9.95 0 0 0 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2z"/>
      </svg>
    </div>
    <div class="cb-header__info">
      <span class="cb-header__name">Adaline</span>
      <span class="cb-header__status">● מחובר</span>
    </div>
    <div class="cb-header__drag-hint">⠿</div>
    <button class="cb-header__close" (click)="close()">✕</button>
  </div>

  <!-- ── Adaline messages (hidden when live chat or the room picker is active) ── -->
  @if (mode !== 'live_chat' && mode !== 'room_picker') {
    <div class="cb-messages" #msgList>
      @for (msg of messages; track trackById($index, msg)) {
        <!-- Bot message -->
        @if (msg.role === 'bot') {
          <div class="cb-msg cb-msg--bot">
            <div class="cb-msg__avatar">A</div>
            <div class="cb-msg__body" [class.cb-msg__body--wide]="!!msg.widget">
              <div class="cb-bubble cb-bubble--bot cb-bubble--hoverable">
                @for (line of lines(msg.text); track line) {
                  <p class="cb-line">{{ line }}</p>
                }
                <!-- Reaction picker — appears on hover -->
                <div class="cb-reaction-picker">
                  @for (emoji of REACTIONS; track emoji) {
                    <button
                      class="cb-reaction-pick-btn"
                      (click)="toggleReaction(msg, emoji)">
                      {{ emoji }}
                    </button>
                  }
                </div>
              </div>
              <!-- Reaction pills -->
              @if (msg.reactions?.length) {
                <div class="cb-reaction-pills">
                  @for (r of msg.reactions; track r) {
                    <button
                      class="cb-reaction-pill"
                      [class.cb-reaction-pill--active]="r.reacted"
                      (click)="toggleReaction(msg, r.emoji)">
                      {{ r.emoji }} {{ r.count }}
                    </button>
                  }
                </div>
              }
              <!-- Rich content (subscription plan cards) -->
              @if (msg.widget === 'contact') {
                <app-adaline-contact-card (openContactForm)="onOpenContactForm()"></app-adaline-contact-card>
              } @else if (msg.widget) {
                <app-adaline-plan-cards
                  [kind]="msg.widget === 'ad_plans' ? 'ad' : 'subscription'"
                  (planSelected)="onPlanSelected($event)">
                </app-adaline-plan-cards>
              }
              <!-- Option chips — only last interactive msg is tappable -->
              @if (msg.options?.length) {
                <div class="cb-options">
                  @for (opt of msg.options; track opt) {
                    <button
                      class="cb-chip"
                      [class.cb-chip--disabled]="!isLastInteractiveMsg(msg) || opt.disabled"
                      (click)="onOptionTap(msg, opt)">
                      {{ opt.label }}
                      @if (opt.value === 'live_chat' && unreadCount > 0) {
                        <span class="cb-chip__badge"
                          >
                          {{ unreadCount }}
                        </span>
                      }
                    </button>
                  }
                </div>
              }
            </div>
          </div>
        }
        <!-- User message -->
        @if (msg.role === 'user') {
          <div class="cb-msg cb-msg--user">
            <div class="cb-bubble cb-bubble--user">{{ msg.text }}</div>
          </div>
        }
      }
      <!-- Typing indicator -->
      @if (isTyping) {
        <div class="cb-msg cb-msg--bot">
          <div class="cb-msg__avatar">A</div>
          <div class="cb-bubble cb-bubble--bot cb-typing">
            <span></span><span></span><span></span>
          </div>
        </div>
      }
    </div>
  }

  <!-- ── Embedded live chat (replaces messages area) ────────────────────── -->
  @if (mode === 'live_chat') {
    <div class="cb-live-chat">
      <app-chat-room
        [embedded]="true"
        [targetRoomId]="selectedRoomId"
        (chatDeleted)="onChatDeleted()">
      </app-chat-room>
    </div>
  }

  <!-- ── Multi-room picker (more than one room — pick which to open) ────── -->
  @if (mode === 'room_picker') {
    <div class="cb-live-chat">
      <app-chat-room-picker
        [rooms]="roomsForPicker"
        (select)="onRoomPicked($event)"
        (delete)="onRoomDeleted($event)">
      </app-chat-room-picker>
    </div>
  }

  <!-- Footer -->
  <div class="cb-footer">
    <button class="cb-back-btn" (click)="backToMenu()">🏠 תפריט ראשי</button>
    <span class="cb-brand">Sale4All · Adaline</span>
  </div>

</div>
